import { AttachmentTable, column, Schema, Table } from "@powersync/web"

/**
 * Keep `project_id` (and, for photo links, the parent record's id) in the
 * upload queue entry of every UPDATE and DELETE. A PATCH only carries the
 * changed columns and a DELETE carries just the id, and by the time the
 * upload monitor looks at a queued delete the local row is gone, so without
 * this a delete couldn't be counted against its project (issue #209).
 */
const trackProjectId = { trackPrevious: { columns: ["project_id"] } }
const trackPhotoLink = (parentColumn: string) => ({
  trackPrevious: { columns: ["project_id", parentColumn] },
})

const project = new Table(
  {
    client_id: column.text,
    code: column.text,
    created_at: column.text,
    created_by: column.text,
    end_date: column.text,
    location_coordinate: column.text,
    location_crs: column.text,
    location_multipolygon: column.text,
    location_name: column.text,
    location_polygon_source: column.text,
    metadata: column.text,
    name: column.text,
    observation_custom_fields: column.text,
    organisation_id: column.text,
    project_template: column.text,
    site_types: column.text,
    start_date: column.text,
    status: column.text,
    type: column.text,
  },
  {
    indexes: {
      id: ["id"],
      name: ["name"],
    },
  },
)

// Synced only by the `projects_list` stream, for client names on the
// projects list.
const client = new Table({
  name: column.text,
  organisation_id: column.text,
  created_at: column.text,
  updated_at: column.text,
})

const project_member = new Table(
  {
    user_id: column.text,
    project_id: column.text,
  },
  {
    indexes: {
      user_id: ["user_id"],
      project_id: ["project_id"],
    },
  },
)

const survey_sites = new Table(
  {
    created_at: column.text,
    created_by: column.text,
    custom_fields: column.text,
    description: column.text,
    location_line: column.text,
    location_point: column.text,
    location_polygon: column.text,
    location_type: column.text,
    name: column.text,
    notes: column.text,
    project_id: column.text,
    recorders: column.text,
    type: column.text,
    updated_at: column.text,
    vegetation_description: column.text,
  },
  { ...trackProjectId, indexes: { project: ["project_id"], name: ["name"] } },
)

const photos = new Table(
  {
    caption: column.text,
    created_at: column.text,
    created_by: column.text,
    file_path: column.text,
    file_size_bytes: column.text,
    filename: column.text,
    height_px: column.text,
    mime_type: column.text,
    organisation_id: column.text,
    original_filename: column.text,
    // NULL until the thumbnail object is stored remotely (plans/019).
    thumbnail_path: column.text,
    updated_at: column.text,
    width_px: column.text,
  },
  { indexes: { organisation: ["organisation_id"] } },
)

const survey_site_photos = new Table(
  {
    created_at: column.text,
    photo_id: column.text,
    project_id: column.text,
    survey_site_id: column.text,
  },
  {
    ...trackPhotoLink("survey_site_id"),
    indexes: {
      project: ["project_id"],
      survey_site: ["survey_site_id"],
      photo: ["photo_id"],
    },
  },
)

const collections = new Table(
  {
    project_id: column.text,
    collection_number: column.text,
    field_name: column.text,
    nano_id: column.text,
    notes: column.text,
    created_by: column.text,
    created_at: column.text,
    updated_at: column.text,
  },
  {
    ...trackProjectId,
    indexes: {
      project: ["project_id"],
      nano_id: ["nano_id"],
    },
  },
)

const observations = new Table(
  {
    project_id: column.text,
    species_id: column.text,
    species_indeterminate: column.integer,
    collection_id: column.text,
    is_collection: column.integer,
    custom_fields: column.text,
    location_coordinate: column.text,
    recorders: column.text,
    survey_site_id: column.text,
    observation_time: column.text,
    notes: column.text,
    created_by: column.text,
    created_at: column.text,
    updated_at: column.text,
  },
  {
    ...trackProjectId,
    indexes: {
      project: ["project_id"],
      species: ["species_id"],
      collection: ["collection_id"],
      survey_site: ["survey_site_id"],
    },
  },
)

const species = new Table(
  {
    name: column.text,
    description: column.text,
    ala_guid: column.text,
    organisation_id: column.text,
    kingdom: column.text,
    family: column.text,
    metadata: column.text,
    created_at: column.text,
  },
  {
    indexes: {
      organisation: ["organisation_id"],
      ala_guid: ["ala_guid"],
    },
  },
)

const project_species = new Table(
  {
    project_id: column.text,
    species_id: column.text,
  },
  {
    indexes: {
      project: ["project_id"],
      species: ["species_id"],
    },
  },
)

const determinations = new Table(
  {
    collection_id: column.text,
    project_id: column.text,
    species_id: column.text,
    species_indeterminate: column.integer,
    notes: column.text,
    determined_by: column.text,
    determined_at: column.text,
    created_at: column.text,
    updated_at: column.text,
  },
  {
    ...trackProjectId,
    indexes: {
      collection: ["collection_id"],
      project: ["project_id"],
      species: ["species_id"],
      determined_at: ["collection_id", "determined_at"],
    },
  },
)

const observation_photos = new Table(
  {
    observation_id: column.text,
    photo_id: column.text,
    project_id: column.text,
    created_at: column.text,
  },
  {
    ...trackPhotoLink("observation_id"),
    indexes: {
      project: ["project_id"],
      observation: ["observation_id"],
      photo: ["photo_id"],
    },
  },
)

const planned_sites = new Table(
  {
    project_id: column.text,
    site_type: column.text,
    name: column.text,
    notes: column.text,
    location_point: column.text,
    survey_site_id: column.text,
    created_by: column.text,
    created_at: column.text,
    updated_at: column.text,
  },
  {
    ...trackProjectId,
    indexes: {
      project: ["project_id"],
      survey_site: ["survey_site_id"],
    },
  },
)

const planned_site_assignments = new Table(
  {
    planned_site_id: column.text,
    project_id: column.text,
    user_id: column.text,
    assigned_by: column.text,
    status: column.text,
    acknowledged_at: column.text,
    created_at: column.text,
    updated_at: column.text,
  },
  {
    // A status-only PATCH carries no planned_site_id, and a deleted
    // assignment can't be looked up: keep it so the upload count can fold the
    // assignment into its planned site.
    ...trackPhotoLink("planned_site_id"),
    indexes: {
      project: ["project_id"],
      planned_site: ["planned_site_id"],
      user: ["user_id"],
    },
  },
)

// Completed GPS tracks, one row per track. `geojson` is the whole LineString
// Feature as JSON text. Recording itself happens in IndexedDB (see
// lib/tracksDb.ts); a track only becomes a row here when it is saved — see
// lib/tracks/publishTracks.ts. Whether a track is drawn on the map is a
// per-device preference and deliberately not a column (see
// useTrackDisplayPrefs).
const tracks = new Table(
  {
    project_id: column.text,
    created_by: column.text,
    geojson: column.text,
    started_at: column.text,
    ended_at: column.text,
    length_m: column.real,
    point_count: column.integer,
    created_at: column.text,
    updated_at: column.text,
  },
  { ...trackProjectId, indexes: { project: ["project_id"] } },
)

// The newest known location of every member of the organisation, across all
// projects, from the session-wide `org_locations` stream. Read-only: a trigger on the server's user_locations
// keeps it current, and the app reports its own location with a direct insert
// (see useRealtimeLocation), never by writing here. geolocation is WKB hex
// text, decoded with parsePostGISPoint.
const user_latest_location = new Table(
  {
    user_id: column.text,
    organisation_id: column.text,
    project_id: column.text,
    geolocation: column.text,
    created_at: column.text,
  },
  { indexes: { project: ["project_id"] } },
)

const geospatialColumns = {
  project_id: column.text,
  created_by: column.text,
  name: column.text,
  notes: column.text,
  geometry_type: column.text,
  geojson: column.text,
  properties: column.text,
  // Server-set colour (issue #139). Synced like any other column since the
  // `geospatial_data` sync rule is `SELECT *`. Device-local colour overrides
  // and the "show label" preference live outside PowerSync entirely — see
  // useGeospatialDisplayPrefs — and must never be added here.
  color: column.text,
  created_at: column.text,
  updated_at: column.text,
}

const geospatial_data = new Table(geospatialColumns, {
  ...trackProjectId,
  indexes: { project: ["project_id"] },
})

/**
 * Local-only mirror of geospatial_data. Rows live here by default on the PWA
 * and are never synced. "Uploading" a record moves it from this table into
 * geospatial_data (same id) inside a single transaction; PowerSync then
 * uploads the insert via the connector.
 */
const local_geospatial_data = new Table(geospatialColumns, {
  localOnly: true,
  indexes: { project: ["project_id"] },
})

const geospatialPhotoColumns = {
  geospatial_data_id: column.text,
  photo_id: column.text,
  project_id: column.text,
  created_at: column.text,
}

const geospatialPhotoIndexes = {
  indexes: {
    project: ["project_id"],
    geospatial_data: ["geospatial_data_id"],
    photo: ["photo_id"],
  },
}

const geospatial_data_photos = new Table(geospatialPhotoColumns, {
  ...trackPhotoLink("geospatial_data_id"),
  ...geospatialPhotoIndexes,
})

/**
 * Local-only mirror of geospatial_data_photos, paired with
 * local_geospatial_data above. A photo attached to a record that hasn't been
 * uploaded yet has nothing on the server to reference, so its association row
 * waits here — inserting it into the synced table would upload a row whose
 * foreign key doesn't resolve. Uploading the record moves both across in the
 * same transaction.
 *
 * The `photos` row itself is synced immediately either way: it stands alone
 * (org-scoped, no geospatial foreign key) and carrying the file metadata up
 * early is what lets the attachment queue start moving the bytes.
 */
const local_geospatial_data_photos = new Table(geospatialPhotoColumns, {
  localOnly: true,
  ...geospatialPhotoIndexes,
})

/**
 * Local-only table for preserving data from failed sync transactions.
 *
 * When the PowerSync connector discards a transaction (permanent server error
 * or exhausted retries), the CRUD entry is removed and the local row may be
 * deleted on the next sync cycle. This table saves the failed operation data
 * so it can be surfaced to the user for retry or editing.
 *
 * Local-only tables are never synced to the server and survive sync cycles.
 */
const sync_failures = new Table(
  {
    target_table: column.text,
    entity_id: column.text,
    op_type: column.text,
    op_data: column.text,
    error_info: column.text,
    failed_at: column.text,
    classification: column.text,
    // JSON of the op's trackPrevious values (see trackProjectId), so a
    // rejected PATCH or DELETE can still be attributed to its project.
    previous_values: column.text,
  },
  {
    localOnly: true,
    indexes: {
      entity: ["entity_id"],
      table_entity: ["target_table", "entity_id"],
    },
  },
)

// Attachment queue markers: one-shot migrations (legacy failure restoration)
// and per-photo automatic-retry marks. Local-only so a default reset keeps
// them.
const attachment_queue_meta = new Table(
  { value: column.text },
  { localOnly: true },
)

// Permanent photo upload failures stay on this device until a retry (manual,
// or automatic once a day). Keeping them separate from AttachmentState.ARCHIVED
// preserves their bytes and lets the upload status UI distinguish failures
// from cached downloads.
const attachment_failures = new Table(
  {
    status: column.integer,
    message: column.text,
    failed_at: column.text,
  },
  { localOnly: true },
)

/**
 * Local-only record of ids that were remapped during upload.
 *
 * A locally-created row can turn out to duplicate one the server already has
 * (e.g. a species with the same name created on another device). The uploader
 * then deletes the local row and points local references at the server's id.
 * Rows parked because the local row failed to upload were queued with the old
 * id, and can be restored — and uploaded — after the remap has run, so the
 * mapping has to outlive the write transaction that performed it.
 */
const sync_id_remaps = new Table(
  {
    target_table: column.text,
    local_id: column.text,
    server_id: column.text,
    created_at: column.text,
  },
  {
    localOnly: true,
    indexes: {
      local: ["target_table", "local_id"],
    },
  },
)

// Project Context. Source headers sync with the always-on project_data stream
// (lightweight — used to list what's available). Occurrences + areas are heavy
// and sync opt-in per source via the `context_records` stream, subscribed by a
// mounted component only when the user enables that source for offline sync.
const context_source = new Table(
  {
    project_id: column.text,
    created_by: column.text,
    kind: column.text,
    source_filename: column.text,
    source_org_name: column.text,
    citation: column.text,
    notes: column.text,
    is_verified: column.integer,
    feature_count: column.integer,
    species_count: column.integer,
    created_at: column.text,
    updated_at: column.text,
  },
  {
    indexes: {
      project: ["project_id"],
      kind: ["kind"],
    },
  },
)

// geometry (and the optional mapped-boundary footprint) is replicated as WKB
// hex text (PostGIS geography) and decoded on the client with the shared wkx
// helper; properties is JSON text.
const context_occurrence = new Table(
  {
    context_source_id: column.text,
    project_id: column.text,
    species_id: column.text,
    observed_on: column.text,
    individual_count: column.integer,
    geometry: column.text,
    footprint: column.text,
    properties: column.text,
    created_at: column.text,
  },
  {
    indexes: {
      source: ["context_source_id"],
      project: ["project_id"],
    },
  },
)

const context_area = new Table(
  {
    context_source_id: column.text,
    project_id: column.text,
    label: column.text,
    class_code: column.text,
    geometry: column.text,
    properties: column.text,
    created_at: column.text,
  },
  {
    indexes: {
      source: ["context_source_id"],
      project: ["project_id"],
    },
  },
)

// Organisation documents linked into the project. The junction + document
// headers sync with the always-on project_data stream (lightweight — used to
// list linked documents alongside context sources). Extracted occurrence +
// survey-site records are heavy and sync opt-in per document via the
// `document_records` stream. Document data is org-canonical: a document linked
// into two projects shares one local copy of its rows.
const project_document = new Table(
  {
    project_id: column.text,
    document_id: column.text,
    organisation_id: column.text,
    created_by: column.text,
    created_at: column.text,
  },
  {
    indexes: {
      project: ["project_id"],
      document: ["document_id"],
    },
  },
)

// Document header/metadata (slim — the columns the mobile UI needs). The
// narrative the Findings tab renders (summary + other_findings) rides here so
// it is readable offline without the heavy extracted records. other_findings
// is a JSONB column replicated as JSON text — parse it with
// parseOtherFindings. location fields and the remaining server-only columns
// (search_tsv, thumbnails) are not synced.
const document = new Table(
  {
    organisation_id: column.text,
    source_filename: column.text,
    title: column.text,
    author: column.text,
    publication_date: column.text,
    document_type: column.text,
    summary: column.text,
    other_findings: column.text,
    source_file_path: column.text,
    occurrence_count: column.integer,
    survey_site_count: column.integer,
    created_at: column.text,
    updated_at: column.text,
  },
  {
    indexes: {
      organisation: ["organisation_id"],
    },
  },
)

// geometry is replicated as WKB hex text (PostGIS geography) and is NULLABLE —
// un-locatable extracted records are retained for the species list but skipped
// by the map adapters. properties is JSON text.
const document_occurrence = new Table(
  {
    document_id: column.text,
    organisation_id: column.text,
    extraction_id: column.text,
    taxon: column.text,
    species_id: column.text,
    latitude: column.real,
    longitude: column.real,
    easting: column.real,
    northing: column.real,
    crs: column.text,
    crs_verbatim: column.text,
    geometry: column.text,
    observed_on: column.text,
    individual_count: column.integer,
    conservation_code: column.text,
    source_page: column.integer,
    verbatim_locality: column.text,
    evidence_text: column.text,
    properties: column.text,
    created_at: column.text,
  },
  {
    indexes: {
      document: ["document_id"],
      species: ["species_id"],
    },
  },
)

const document_survey_site = new Table(
  {
    document_id: column.text,
    organisation_id: column.text,
    extraction_id: column.text,
    site_type: column.text,
    recorded_on: column.text,
    latitude: column.real,
    longitude: column.real,
    easting: column.real,
    northing: column.real,
    crs: column.text,
    crs_verbatim: column.text,
    geometry: column.text,
    source_page: column.integer,
    verbatim_locality: column.text,
    evidence_text: column.text,
    properties: column.text,
    created_at: column.text,
  },
  {
    indexes: {
      document: ["document_id"],
    },
  },
)

export const AppSchema = new Schema({
  project,
  client,
  project_member,
  survey_sites,
  photos,
  survey_site_photos,
  species,
  project_species,
  collections,
  determinations,
  observations,
  observation_photos,
  tracks,
  user_latest_location,
  geospatial_data,
  local_geospatial_data,
  geospatial_data_photos,
  local_geospatial_data_photos,
  sync_failures,
  attachment_failures,
  attachment_queue_meta,
  sync_id_remaps,
  planned_sites,
  planned_site_assignments,
  context_source,
  context_occurrence,
  context_area,
  project_document,
  document,
  document_occurrence,
  document_survey_site,
  attachments: new AttachmentTable(),
  // Thumbnail queue (plans/019), kept apart from originals; see photoThumbnails.ts.
  thumbnail_attachments: new AttachmentTable({
    viewName: "thumbnail_attachments",
  }),
})

export type PowerSyncAppDatabase = (typeof AppSchema)["types"]
export type PowerSyncProjectRow = PowerSyncAppDatabase["project"]
export type PowerSyncSurveySiteRow = PowerSyncAppDatabase["survey_sites"]
export type PowerSyncPhotoRow = PowerSyncAppDatabase["photos"]
export type PowerSyncSurveySitePhotoRow =
  PowerSyncAppDatabase["survey_site_photos"]
export type PowerSyncSpeciesRow = PowerSyncAppDatabase["species"]
export type PowerSyncProjectSpeciesRow = PowerSyncAppDatabase["project_species"]
export type PowerSyncCollectionRow = PowerSyncAppDatabase["collections"]
export type PowerSyncDeterminationRow = PowerSyncAppDatabase["determinations"]
export type PowerSyncObservationRow = PowerSyncAppDatabase["observations"]
export type PowerSyncObservationPhotoRow =
  PowerSyncAppDatabase["observation_photos"]
export type PowerSyncTrackRow = PowerSyncAppDatabase["tracks"]
export type PowerSyncUserLatestLocationRow =
  PowerSyncAppDatabase["user_latest_location"]
export type PowerSyncGeospatialDataRow = PowerSyncAppDatabase["geospatial_data"]
export type PowerSyncLocalGeospatialDataRow =
  PowerSyncAppDatabase["local_geospatial_data"]
export type PowerSyncGeospatialDataPhotoRow =
  PowerSyncAppDatabase["geospatial_data_photos"]
export type PowerSyncPlannedSiteRow = PowerSyncAppDatabase["planned_sites"]
export type PowerSyncPlannedSiteAssignmentRow =
  PowerSyncAppDatabase["planned_site_assignments"]
export type PowerSyncContextSourceRow = PowerSyncAppDatabase["context_source"]
export type PowerSyncContextOccurrenceRow =
  PowerSyncAppDatabase["context_occurrence"]
export type PowerSyncContextAreaRow = PowerSyncAppDatabase["context_area"]
export type PowerSyncProjectDocumentRow =
  PowerSyncAppDatabase["project_document"]
export type PowerSyncDocumentRow = PowerSyncAppDatabase["document"]
export type PowerSyncDocumentOccurrenceRow =
  PowerSyncAppDatabase["document_occurrence"]
export type PowerSyncDocumentSurveySiteRow =
  PowerSyncAppDatabase["document_survey_site"]
